import type { Maybe } from '@/types/globals';
import { Injectable } from '@angular/core';
import { AppenderType, type IRemoteConfig, LogLevel, Timber } from '@itero/timber';
import { SessionType, ViewModeEnum } from '@shared/models/consts';
import { EnvironmentParams } from '@shared/models/environment-params';
import { RxModel } from '@shared/models/rx-models/interfaces/rx-model';
import { HostPlatformService } from '@shared/services/host-platform.service';
import { SoftwareOptionsService } from '@shared/services/software-options.service';
import { AuthInfoQuery } from '@shared/store/authInfo/auth-info-query';
import { v4 as uuid } from 'uuid';
import { AppSettingsService } from '../app-settings/app-settings.service';
import { BiEventType, BiObject, BiObjectName, BiStage } from './interfaces/bi.enums';
import { BiEvent, ISessionStartEvent, IStageReportEvent, ITags, IUserActionEvent } from './interfaces/bi.events';
import { IntuitiveFlowService } from '@shared/services/intuitive-flow.service';
import { configureObservability, createBiReporter, ObservabilityConfig } from '@itero/observability';
import { isIteroEnvironment, isIteroRegion } from '@itero/client-sd';
import { ShellQuery } from '@shared/store/shell/shell-query';
import { RegionClusterMapper } from '@shared/utils/region-cluster-mapper';
import { LdFeatureFlagsService } from '@shared/services/ld-feature-flags.service';
import { ApplicationModes, FeatureToggleSettings, SoftwareOptions } from '@shared/models/enums/enums';
import { BiSessionInfo } from '@itero/rx-attachments/lib/models/analytics/BiSessionInfo';
import { FeatureToggle } from '@shared/models/feature-toggle';
import { combineLatest, first, Observable, of, ReplaySubject, switchMap } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class BiLoggerService {
	protected biTimber: Timber | null = null;
	private biReporter: ReturnType<typeof createBiReporter> | null = null;
	private rxSessionId: Maybe<string>;
	private initialized = false;
	private centralizedBiSessions = false;

	private readonly observabilityConfigured$ = new ReplaySubject<void>(1);
	readonly ready$ = this.observabilityConfigured$.asObservable();

	readonly biSessionInfo$: Observable<BiSessionInfo> = this.ready$.pipe(
		switchMap(() => combineLatest([this.authInfoQuery.authInfo$, this.shellQuery.appSettings$, this.shellQuery.appBISessionId$])),
		map(
			([authInfo, appSettings, appBISessionId]) =>
				({
					sessionId: this.centralizedBiSessions ? appBISessionId ?? authInfo?.sessionId : authInfo?.sessionId,
					sessionType: authInfo?.sessionType,
					biTimberUrl: appSettings?.biTimberUrl
				}) as BiSessionInfo
		)
	);

	constructor(
		private readonly appSettingsService: AppSettingsService,
		private readonly authInfoQuery: AuthInfoQuery,
		private readonly intuitiveFlowService: IntuitiveFlowService,
		private readonly shellQuery: ShellQuery,
		private readonly softwareOptionsService: SoftwareOptionsService,
		protected hostPlatformService: HostPlatformService,
		private readonly regionClusterMapper: RegionClusterMapper,
		private readonly ldFeatureFlagsService: LdFeatureFlagsService
	) {
		this.initIfReady();
	}

	static getConfig(environmentParams: EnvironmentParams): IRemoteConfig {
		const { appId, requiredFields, eventType } = environmentParams.logging;

		return {
			url: environmentParams.biTimberUrl ?? '',
			appId: appId ?? 'rx-app',
			requiredFields,
			minLogLevel: LogLevel.All,
			appenderTypes: [AppenderType.BIRemote],
			eventType: eventType ?? undefined
		} satisfies IRemoteConfig;
	}

	/**
	 * Called by app.facade on every MFE open, after all stores are populated.
	 * Resolves feature flags (async for scanner, sync for web), configures
	 * observability, fires sessionStartEvent, and starts tracking the view-mode tag.
	 * Returns an Observable that completes after initialization; errors are caught
	 * internally so the caller's stream is never broken.
	 */
	initializeObservability(rxId: string | undefined): Observable<undefined> {
		this.centralizedBiSessions = false;

		return combineLatest([
			this.intuitiveFlowService.isIntuitiveFlow$,
			this.softwareOptionsService.hasCompanySoftwareOption$(SoftwareOptions.UnifiedPatientsEnabled)
		]).pipe(
			first(),
			switchMap(([isIntuitive, hasUnifiedPatients]) => {
				if (!((isIntuitive || hasUnifiedPatients) && this.shellQuery.appBISessionId)) {
					return of(null);
				}

				return this.getLDFeatureFlags();
			}),
			tap(featureToggles => {
				if (featureToggles !== null && this.tryUseCentralizedBiSessions(featureToggles)) {
					this.centralizedBiSessions = true;
				}

				this.observabilityConfigured$.next();

				if (!this.centralizedBiSessions) {
					// send events using old schema v2
					this.sessionStartEvent(
						rxId ?? '',
						this.shellQuery.contactId ?? 0,
						this.shellQuery.companyId ?? 0,
						this.shellQuery.rxSessionId ?? undefined,
						this.shellQuery.orderId ?? undefined
					);
				}

				this.trackViewModeAndRxId(rxId);
			}),
			map(() => undefined),
			catchError(e => {
				/* eslint-disable no-console */
				console.error('[BiLogger] Failed to initialize observability', e);
				this.observabilityConfigured$.next(); // ensure ready$ still emits

				return of(undefined);
			})
		);
	}

	destroyObservability(): void {
		if (this.biReporter) {
			this.biReporter.dispose();
			this.biReporter = null;
		}

		configureObservability('rx-app', { centralizedBiSessions: false, autocapture: false });
		this.centralizedBiSessions = false;
		this.rxSessionId = undefined;
	}

	sendSaveRxBiLogs(rx: RxModel): void {
		const { IsDraft: isDraft, MultiBiteRestoScan: multiBiteRestoScan } = rx;

		this.stageReportEvent({
			object: BiObject.Attachments,
			stage: BiStage.Save,
			previous: 'draft',
			description: BiObjectName.IsDraft,
			trigger: String(!!isDraft)
		});

		this.stageReportEvent({
			object: BiObject.ScanOptions,
			stage: BiStage.Save,
			previous: 'draft',
			description: multiBiteRestoScan?.Includes?.join(', ') || '',
			trigger: ''
		});
	}

	sessionStartEvent(rxId: string, user: number, companyId: number, rxSessionId?: string, orderId?: number): void {
		this.rxSessionId = rxSessionId;
		this.sendBiEvent({
			event: {
				externalSessionId: this.authInfoQuery.authInfo?.sessionId ?? uuid(),
				tags: {
					userId: String(user),
					companyId: String(companyId),
					rxId,
					orderId: orderId ? String(orderId) : null
				} as ITags
			} as ISessionStartEvent,
			type: BiEventType.SessionStart
		});
	}

	userActionEvent(event: IUserActionEvent): void {
		this.sendBiEvent({
			event,
			type: BiEventType.UserAction
		} as BiEvent);
	}

	stageReportEvent(event: IStageReportEvent): void {
		this.sendBiEvent({
			event,
			type: BiEventType.StageReport
		} as BiEvent);
	}

	get reporter(): ReturnType<typeof createBiReporter> | null {
		const sessionId = this.activeSessionId;

		if (!sessionId) {
			return null;
		}

		return this.getOrCreateReporter(sessionId);
	}

	protected createBi(environmentParams: EnvironmentParams): void {
		if (this.appSettingsService.isValidConfig('biTimberUrl')) {
			this.biTimber = new Timber(BiLoggerService.getConfig(environmentParams));
		} else {
			this.biTimber = null;
			/* eslint-disable no-console */
			console.error('BI object was not created');
		}
	}

	private get activeSessionId(): string | null | undefined {
		return this.centralizedBiSessions ? this.shellQuery.appBISessionId : this.rxSessionId;
	}

	protected sendBiEvent(biEvent: BiEvent) {
		this.initIfReady();

		const sessionId = this.activeSessionId;

		const stringEvent = JSON.stringify(biEvent.event);

		if (sessionId && this.biTimber) {
			this.biTimber.biLog(
				LogLevel.All,
				{},
				{
					extendedParameters: {
						sessionId,
						sessionType: this.hostPlatformService.isScanner ? SessionType.Desktop : SessionType.Web,
						component: 'RX',
						type: biEvent.type,
						additionalFields: JSON.parse(stringEvent)
					}
				}
			);
		}
	}

	private initIfReady(): void {
		if (this.initialized) {
			// init logger once app.settinsg are loaded
			// every service's method checks/initialize timber before execute its methods
			return;
		}

		const environmentParams = this.appSettingsService.environmentParams;

		if (!environmentParams) {
			return;
		}

		this.initialized = true;
		this.createBi(environmentParams);
	}

	private getObservabilityConfig(featureToggles: FeatureToggle[]): ObservabilityConfig {
		const config: ObservabilityConfig = { centralizedBiSessions: false, autocapture: false };

		for (const toggle of featureToggles) {
			if (toggle?.id === (FeatureToggleSettings.CentralizedBiSessions as string) && toggle.isActive) {
				config.centralizedBiSessions = true;
			} else if (toggle?.id === (FeatureToggleSettings.ObservabilityAutocapture as string) && toggle.isActive) {
				config.autocapture = true;
			}
		}

		return config;
	}

	private tryUseCentralizedBiSessions(featureToggles: FeatureToggle[]): boolean {
		const config = this.getObservabilityConfig(featureToggles);

		try {
			configureObservability('rx-app', config);
		} catch (e) {
			/* eslint-disable no-console */
			console.error('[BiLogger] Failed to configure observability', e);
		}

		return config.centralizedBiSessions ?? false;
	}

	private trackViewModeAndRxId(rxId: string | undefined): void {
		try {
			const viewMode = this.shellQuery.viewMode;

			void this.reporter?.trackSessionTags({
				tags: { viewMode: viewMode ?? ViewModeEnum.ViewRx, rxId: rxId ?? null }
			});
		} catch (e) {
			/* eslint-disable no-console */
			console.error('[BiLogger] Failed to send sessionTags with viewMode', e);
		}
	}

	private getLDFeatureFlags() {
		return this.ldFeatureFlagsService.getLDFeatureFlagsIfScanner(this.shellQuery.applicationMode as ApplicationModes).pipe(first());
	}

	/**
	 * BiLoggerService is a root-scoped singleton that outlives individual MFE
	 * opens — the custom element's injector persists for the whole page lifetime,
	 * even as the host removes and re-adds the element to the DOM. A BiReporter's
	 * sessionId is fixed for its lifetime, so when the MFE re-opens with a new
	 * appBISessionId we must dispose the old reporter and create a fresh one.
	 * Within the same session the reporter is reused.
	 */
	private getOrCreateReporter(sessionId: string): ReturnType<typeof createBiReporter> | null {
		if (this.biReporter && this.biReporter.sessionId === sessionId) {
			return this.biReporter;
		}

		if (this.biReporter) {
			this.biReporter.dispose();
		}

		const env = this.appSettingsService.environmentParams?.signalFx?.environment;
		const region = this.regionClusterMapper.map(this.shellQuery.getValue().region);

		if (!env || !region || !isIteroEnvironment(env) || !isIteroRegion(region)) {
			return null;
		}

		try {
			this.biReporter = createBiReporter({
				env,
				region,
				component: 'rx-app',
				sessionType: this.hostPlatformService.isScanner ? SessionType.Desktop : SessionType.Web,
				sessionId
			});
		} catch {
			this.biReporter = null;

			return null;
		}

		return this.biReporter;
	}
}
